import React, { useState } from "react";
import {
  Modal,
  Card,
  Space,
  Typography,
  Avatar,
  Button,
  Tag,
  Table,
  InputNumber,
  message,
  Select,
  Input,
} from "antd";
import {
  WalletOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import QrCode from "./QrCode";

const { Title, Text } = Typography;

const WalletModal = ({
  open,
  user,
  wallet,
  transactions,
  onClose,
  onAddFunds,
  addFundsLoading,
}) => {
  const navigate = useNavigate();
  const [transactionType, setTransactionType] = useState("credit");
  const [amount, setAmount] = useState(null);
  const [reason, setReason] = useState("MANUAL_ADJUSTMENT");
  const [note, setNote] = useState("");

  const formatLabel = (v) =>
    v ? v.replaceAll("_", " ") : "—";

  const handleSubmit = async () => {
    if (!amount || amount <= 0) {
      message.warning("Enter a valid amount");
      return;
    }

    if (!reason) {
      message.warning("Select a reason");
      return;
    }

    try {
      await onAddFunds({
        amount,
        reason,
        note,
        transactionType,
      }); // 🔥 waits for updated wallet
      setAmount(null);
      setNote("");
      setReason("MANUAL_ADJUSTMENT");
    } catch {
      // error already handled
    }
  };

  return (
    <Modal
      open={open}
      destroyOnClose
      onCancel={onClose}
      footer={null}
      centered
      width="80vw"
      closable={false}
      zIndex={1100}
      bodyStyle={{
        padding: 0,
        borderRadius: 16,
        // overflow: "hidden",
        background: "#f8fafc",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* ===== HEADER ===== */}
        <div
          style={{
            padding: "20px 28px",
            background: "linear-gradient(135deg, #eff6ff, #dbeafe)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Space>
            <Avatar
              size={44}
              icon={<WalletOutlined />}
              style={{
                background: "linear-gradient(135deg, #2563eb, #3b82f6)",
              }}
            />
            <div>
              <Title level={4} style={{ margin: 0 }}>
                Wallet Overview
              </Title>
              <Text type="secondary">{user?.full_name || "User"}</Text>
            </div>
          </Space>

          <Button shape="circle" icon={<CloseOutlined />} onClick={onClose} />
        </div>

        {/* ===== BODY ===== */}
        <div style={{ padding: "28px 0" }}>
          {/* ===== BALANCE ===== */}
          <Card
            bordered={false}
            style={{
              borderRadius: 18,
              background: "linear-gradient(135deg, #ecfeff, #cffafe)",
              boxShadow: "0 10px 26px rgba(0,0,0,.08)",
              marginBottom: 18,
            }}
          >

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <Text type="secondary">Available Balance</Text>

                <Title level={1} style={{ margin: "6px 0" }}>
                  NZ$ {wallet?.account_balance ?? 0}
                </Title>



                <Space size={6} style={{ marginTop: 6 }}>
                  <ClockCircleOutlined />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {wallet?.last_updated
                      ? new Date(wallet?.last_updated).toLocaleString()
                      : "—"}
                  </Text>
                </Space>
              </div>
              <QrCode
                qrCodeUrl={wallet?.qr_code}
                user={user}
                payId={wallet?.pay_id}
              />
            </div>
          </Card>

          {/* ===== ADD FUNDS CARD ===== */}
          <Card
            bordered={false}
            style={{
              borderRadius: 18,
              background:
                transactionType === "credit"
                  ? "linear-gradient(135deg, #eff6ff, #dbeafe)"
                  : "linear-gradient(135deg, #fef2f2, #fee2e2)",
              boxShadow: "0 8px 24px rgba(0,0,0,.08)",
              marginBottom: 24,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 24,
                flexWrap: "wrap",
              }}
            >
              {/* LEFT SIDE */}
              <div style={{ flex: 1, minWidth: 260 }}>
                <Space align="start">
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: 14,
                      background:
                        transactionType === "credit"
                          ? "linear-gradient(135deg, #2563eb, #3b82f6)"
                          : "linear-gradient(135deg, #dc2626, #ef4444)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: 22,
                    }}
                  >
                    {transactionType === "credit" ? (
                      <ArrowDownOutlined />
                    ) : (
                      <ArrowUpOutlined />
                    )}
                  </div>

                  <div>
                    <Title level={5} style={{ marginBottom: 2 }}>
                      {transactionType === "credit"
                        ? "Credit Wallet"
                        : "Debit Wallet"}
                    </Title>

                    <Text type="secondary">
                      {transactionType === "credit"
                        ? "Add funds into user wallet"
                        : "Deduct funds from user wallet"}
                    </Text>

                    <div style={{ marginTop: 14 }}>
                      <Select
                        value={transactionType}
                        onChange={(value) => setTransactionType(value)}
                        style={{ width: 180 }}
                        popupMatchSelectWidth
                        getPopupContainer={(trigger) => trigger.parentNode}
                        dropdownStyle={{ zIndex: 9999 }}
                        options={[
                          {
                            label: "Credit Funds",
                            value: "credit",
                          },
                          {
                            label: "Debit Funds",
                            value: "debit",
                          },
                        ]}
                      />
                    </div>
                  </div>
                </Space>
              </div>

              {/* RIGHT SIDE */}
              <div
                style={{
                  flex: 2,
                  minWidth: 320,
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                }}
              >
                {/* Amount */}
                <InputNumber
                  value={amount}
                  onChange={setAmount}
                  min={0}
                  step={0.01}
                  size="large"
                  style={{ width: "100%" }}
                  prefix="NZ$"
                  placeholder={
                    transactionType === "credit"
                      ? "Enter credit amount"
                      : "Enter debit amount"
                  }
                  parser={(value) => value.replace(/[^\d.]/g, "")}
                />

                {/* Balance Preview */}
                {/* {amount > 0 && (
                  <div
                    style={{
                      padding: "10px 14px",
                      borderRadius: 10,
                      background: "#fff",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <Text type="secondary">
                      Current Balance:
                    </Text>

                    <br />

                    <Text strong>
                      NZ$ {Number(wallet?.account_balance || 0).toFixed(2)}
                    </Text>

                    <br />

                    <Text type="secondary">
                      Balance After Transaction:
                    </Text>

                    <br />

                    <Text
                      strong
                      style={{
                        color:
                          transactionType === "credit"
                            ? "#16a34a"
                            : "#dc2626",
                      }}
                    >
                      NZ${" "}
                      {transactionType === "credit"
                        ? (
                          Number(wallet?.account_balance || 0) +
                          Number(amount || 0)
                        ).toFixed(2)
                        : (
                          Number(wallet?.account_balance || 0) -
                          Number(amount || 0)
                        ).toFixed(2)}
                    </Text>
                  </div>
                )} */}

                {/* Reason */}
                <Select
                  value={reason}
                  onChange={(value) => setReason(value)}
                  placeholder="Select reason"
                  size="large"
                  popupMatchSelectWidth
                  getPopupContainer={(trigger) => trigger.parentNode}
                  dropdownStyle={{ zIndex: 9999 }}
                  options={[
                    {
                      label: "Manual Adjustment",
                      value: "MANUAL_ADJUSTMENT",
                    },
                    {
                      label: "Refund",
                      value: "MANUAL_REFUND",
                    },
                    {
                      label: "Promotional Reward",
                      value: "PROMOTIONAL_REWARD",
                    },
                    {
                      label: "Penalty",
                      value: "PENALTY",
                    },
                  ]}
                />

                {/* Note */}
                <Input.TextArea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional note for audit trail"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  maxLength={180}
                />

                {/* Submit */}
                <Button
                  type="primary"
                  size="large"
                  loading={addFundsLoading}
                  onClick={handleSubmit}
                  danger={transactionType === "debit"}
                  icon={
                    transactionType === "credit" ? (
                      <PlusOutlined />
                    ) : (
                      <ArrowUpOutlined />
                    )
                  }
                  style={{
                    borderRadius: 12,
                    height: 46,
                    fontWeight: 600,
                  }}
                >
                  {transactionType === "credit"
                    ? "Credit Funds"
                    : "Debit Funds"}
                </Button>
              </div>
            </div>
          </Card>

          {/* ===== TRANSACTIONS ===== */}
          <Card
            bordered={false}
            title={
              <Space style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}><WalletOutlined />
                  Recent Wallet Transactions</div>
                <Button
                  onClick={() => navigate("/transaction-manager")}
                >
                  View All
                </Button>
              </Space>
            }

            style={{
              borderRadius: 14,
              boxShadow: "0 6px 18px rgba(0,0,0,.06)",
            }}
          >
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={[...(transactions || [])]}
              locale={{
                emptyText: "No wallet transactions found",
              }}
              columns={[
                {
                  title: "Date",
                  dataIndex: "created_at",
                  render: (v) => (
                    <Text type="secondary">{new Date(v).toLocaleString()}</Text>
                  ),
                },
                {
                  title: "Type",
                  render: (_, record) => {
                    const type = record?.my_entry?.entry_type;
                    return (
                      <Tag
                        color={type === "CREDIT" ? "green" : "red"}
                        icon={type === "CREDIT" ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                      >
                        {type}
                      </Tag>
                    );
                  },
                },
                {
                  title: "Amount",
                  align: "right",
                  render: (_, record) => {
                    const entry = record?.my_entry;
                    if (!entry) return "—";

                    return (
                      <Text
                        strong
                        style={{
                          color:
                            entry.entry_type === "CREDIT"
                              ? "#16a34a"
                              : "#dc2626",
                        }}
                      >
                        ₹{Number(entry.amount).toFixed(2)}
                      </Text>
                    );
                  },
                },
                {
                  title: "Reason",
                  render: (_, record) => {
                    const reason = record?.meta?.reason;
                    const note = record?.meta?.note;

                    return (
                      <Space direction="vertical" size={0}>
                        <Text strong>
                          {reason ? reason.replaceAll("_", " ") : "—"}
                        </Text>
                        {note && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {note}
                          </Text>
                        )}
                      </Space>
                    );
                  },
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  render: (status) => (
                    <Tag color={status === "SUCCESS" ? "green" : "orange"}>
                      {status}
                    </Tag>
                  ),
                },
                {
                  title: "Reference",
                  dataIndex: "reference_number",
                  ellipsis: true,
                },
              ]}
            />
          </Card>
        </div>
      </motion.div>
    </Modal>
  );
};

export default WalletModal;
